import { ExpandLessRounded, ExpandMoreRounded } from "@mui/icons-material";
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  Box,
  Chip,
  IconButton,
  Link,
  AccordionSummary,
  Stack,
  styled,
  SxProps,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { AvatarGroup, BlockLink } from "src/components";
import { Links, SearchFilter } from "src/constants";
import { ITeamNetworkRole } from "src/models";

const Accordion = styled(MuiAccordion)`
  min-height: auto;
  transition: ${({ theme }) => theme.transitions.create("background-color")};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.transparent5};
  }

  .MuiAccordionSummary-content {
    margin: 0;
  }
`;

interface IProps {
  role: ITeamNetworkRole;
  sx?: SxProps;
}

const TeamNetworkRoleListItem = (props: IProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Accordion disableGutters expanded={isExpanded} sx={{ backgroundColor: "transparent", p: 0 }}>
      <AccordionSummary sx={{ p: 0 }}>
        <BlockLink href={Links.Role(props.role.guid)}>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              ...props.sx,
              p: 1
            }}>
            <Stack spacing={0.5}>
              <Stack spacing={0.5} direction="row" sx={{ pb: 0.25 }}>
                <Chip label="Role" color="role" />
                {props.role.isOpen && (
                  <Chip
                    label={`OPEN as of ${props.role.isOpenLastModifiedUtc
                      .toLocal()
                      .toLocaleString(DateTime.DATE_MED)
                      .toLocaleUpperCase()}`}
                    color="role"
                  />
                )}
              </Stack>
              <Stack>
                <Typography variant="h5">{props.role.name}</Typography>
                <Typography variant="body1" sx={{ wordWrap: "break-word" }}>
                  {props.role.description}
                </Typography>
              </Stack>

              <AvatarGroup users={props.role.members} max={10} />
            </Stack>

            <IconButton component={Box} onClick={() => setIsExpanded(!isExpanded)} sx={{ zIndex: 2 }}>
              {isExpanded ? <ExpandLessRounded fontSize="large" /> : <ExpandMoreRounded fontSize="large" />}
            </IconButton>
          </Stack>
        </BlockLink>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={0.5} sx={{ mx: 1 }}>
          <Box>
            <Typography variant="overline">Accountabilities</Typography>
            {props.role.accountabilities && props.role.accountabilities.length > 0 ? (
              <Stack sx={{ pl: 1, mt: 0.5 }}>
                {props.role.accountabilities.map((accountability, index) => (
                  <Typography key={index} sx={{
                    display: "list-item"
                  }}>
                    {accountability.description}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="placeholder" sx={{ mt: 0.5 }}>
                No accountabilities defined.
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="overline">Tags</Typography>
            {props.role.tags && props.role.tags.length > 0 ? (
              <Stack direction="row" divider={<Typography>,&nbsp;</Typography>} sx={{ mt: 0.5 }}>
                {props.role.tags.map((tag, i) => (
                  <Link key={i} href={Links.Search(tag, SearchFilter.Roles)}>
                    {tag}
                  </Link>
                ))}
              </Stack>
            ) : (
              <Typography variant="placeholder" sx={{ mt: 0.5 }}>
                No tags defined.
              </Typography>
            )}
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export { TeamNetworkRoleListItem };
